import { inject } from 'mobx-react';
import * as React from 'react';

import { LocalizedText } from 'bernie-l10n';
import { ViewLarge, ViewMedium, Viewport, ViewSmall } from 'bernie-view';

import { CurrentHotelStore } from 'stores/current-hotel-store';

import { AmenityViewModel } from 'models';

import { classNames } from 'uitk-react-utils';
import { UitkIcon, UitkIconSize } from 'uitk-react-icons';

export interface RoomAmenitiesProps {
  amenityItemClasses?: string;
  className?: string;
  roomAmenities?: AmenityViewModel[];
  listClasses?: string[];
  iconSize?: UitkIconSize;
  currentHotel?: CurrentHotelStore;
}

@inject('currentHotel')
export class RoomAmenities extends React.PureComponent<RoomAmenitiesProps> {
  public static defaultProps: Partial<RoomAmenitiesProps> = {
    iconSize: UitkIconSize.MEDIUM,
    listClasses: ['no-bullet', 'uitk-type-200', 'pwa-theme--grey-700'],
  };

  public render() {
    const { className, roomAmenities, iconSize, listClasses, amenityItemClasses, currentHotel } = this.props;

    if (!roomAmenities || roomAmenities.length === 0) {
      return null;
    }

    const defaultProps = {
      className,
      roomAmenities,
      iconSize,
      listClasses,
      amenityItemClasses,
    };

    return (
      <>
        <h4 className="is-visually-hidden">
          <LocalizedText message="hotels.infosite.policies.roomAmenities.title" />
        </h4>
        <Viewport>
          <ViewSmall>
            <AmenitiesList {...defaultProps} />
          </ViewSmall>
          <ViewLarge>
            <AmenitiesList {...defaultProps} displayMultipleRatePlansInline={currentHotel?.canDisplayMultipleRatePlansInline} />
          </ViewLarge>
        </Viewport>
      </>
    );
  }
}

interface AmenityItemProps {
  amenity: AmenityViewModel;
  amenityItemClasses?: string;
  iconSize: UitkIconSize;
  index: number;
}

export const AmenityItem = ({ amenity, amenityItemClasses, iconSize = UitkIconSize.MEDIUM, index }: AmenityItemProps) => {
  if (amenity && amenity.description) {
    const defaultClasses = 'uitk-cell s-cell-1-3 m-cell-1-3 l-cell-1-2 xl-cell-1-2 s-y-margin-two m-y-margin-two';

    return (
      <li className={amenityItemClasses ? amenityItemClasses : defaultClasses} key={`amenity-${index}-${amenity.description}`}>
        <UitkIcon id={`amenity-icon-${amenity.icon}`} name={amenity.icon} size={iconSize} aria-hidden="true" />
        <Viewport>
          <ViewSmall>
            <span className="amenities-icon-description">{amenity.description}</span>
          </ViewSmall>
          <ViewMedium>
            <span className="all-l-padding-two">{amenity.description}</span>
          </ViewMedium>
        </Viewport>
      </li>
    );
  }

  return null;
};

AmenityItem.displayName = 'AmenityItem';

interface AmenityListProps {
  className?: string;
  roomAmenities?: AmenityViewModel[];
  iconSize: UitkIconSize;
  listClasses?: string[];
  amenityItemClasses?: string;
  displayMultipleRatePlansInline?: boolean;
}

export const AmenitiesList = ({
  className,
  roomAmenities,
  iconSize,
  listClasses,
  amenityItemClasses,
  displayMultipleRatePlansInline,
}: AmenityListProps) => {
  const ulClasses = displayMultipleRatePlansInline
    ? classNames(className, `uitk-grid no-bullet uitk-type-300 pwa-theme--grey-700 all-t-padding-one`)
    : classNames(className, 'all-t-padding-one', listClasses, { 'uitk-grid': roomAmenities.length > 3 });

  return (
    <ul className={`room-amenities ${ulClasses}`}>
      {roomAmenities &&
        roomAmenities.map((amenity, index) => (
          <AmenityItem amenity={amenity} amenityItemClasses={amenityItemClasses} index={index} key={index} iconSize={iconSize} />
        ))}
    </ul>
  );
};
